import type { VoiceSpace } from '../../content/voices/voice-spaces.js';
/** A mixer channel: every sound plays into one, and the mixer sets its level (`audio-mixer.ts`). */
export type AudioBus = 'music' | 'effects' | 'voices';
const BUSES: AudioBus[] = ['music', 'effects', 'voices'];

/** A sound that keeps playing until stopped; `level` eases its own volume (0..1, before the bus) without a click. */
export interface LoopHandle { level(volume: number): void; stop(): void }
/** A long track played from its file as it downloads, for music too large to decode whole. */
export interface StreamHandle { readonly element: HTMLAudioElement; level(volume: number): void }
export interface PlayOptions {
  bus?: AudioBus; volume?: number; rate?: number; pan?: number;
  /** A hall reverb tail rings on after the sound. */
  reverb?: boolean;
  /** A room of its own the sound rings in (an ancestry's voice, `voice-spaces.ts`). */
  space?: VoiceSpace;
  /** Seconds to fade in and out; `start` skips into the file and `length` cuts the sound after that many seconds. */
  fade?: { in: number; out: number; start?: number; length?: number };
  /** Play at the engine's matched loudness (`match`) rather than as the file was recorded. */
  matched?: boolean;
}
export interface ControlledOptions { delay: number; offset: number; seconds: number; loop: boolean; volume: number; pan: number }
/** Stops a one-shot, fading it out over `fade` seconds first so a replaced sound never clicks. */
export type StopSound = (fade?: number) => void;

/** Matched recordings are evened out to this loudness (the loudest 400 ms, RMS); `mix.ts` sets each kind's level from there. */
const MATCH_DBFS = -14, MATCH_WINDOW = .4, MATCH_HOP = .1;
/** No recording is boosted or cut further than this (the night bed, recorded at about -60 dBFS, needs the most), so a silent file is not made a roar of noise. */
const MATCH_BOOST_DB = 48, MATCH_CUT_DB = -20;
/** The loudest 400 ms of a buffer (RMS of its channels together), in dBFS; -Infinity for silence. */
export function loudness(buffer: Pick<AudioBuffer, 'length' | 'sampleRate' | 'numberOfChannels' | 'getChannelData'>): number {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i)), n = buffer.length;
  if (!n || !channels.length) return -Infinity;
  const energy = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) { let sum = 0; for (const data of channels) sum += data[i]; const mono = sum / channels.length; energy[i + 1] = energy[i] + mono * mono; }
  const window = Math.min(n, Math.max(1, Math.round(MATCH_WINDOW * buffer.sampleRate))), hop = Math.max(1, Math.round(MATCH_HOP * buffer.sampleRate));
  let loudest = 0;
  for (let start = 0; ; start += hop) {
    const end = Math.min(n, start + window);
    loudest = Math.max(loudest, (energy[end] - energy[end - window]) / window);
    if (end === n) break;
  }
  return loudest > 0 ? 10 * Math.log10(loudest) : -Infinity;
}
/** The gain that brings a recording of this loudness to the matched level. */
export const matchGain = (dbfs: number) => Number.isFinite(dbfs) ? 10 ** (Math.max(MATCH_CUT_DB, Math.min(MATCH_BOOST_DB, MATCH_DBFS - dbfs)) / 20) : 1;

const defaultContext = () => typeof AudioContext === 'undefined' ? undefined : new AudioContext();
const defaultLoad = async (url: string) => {
  const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
  return response.ok ? response.arrayBuffer() : undefined;
};

/**
 * The one audio graph of the page. Every sound the browser makes goes through here: a single context, resumed by the page's first
 * gesture, with a gain per bus that the mixer drives, so the volume sliders and the mute button reach everything at once, including
 * sounds already playing. Effects, voices and ambience are decoded once and played from memory (sample-exact loops, no media-element
 * gaps); music streams from its file but is routed through its bus all the same. Nothing else may create an `Audio` or `AudioContext`
 * (`tests/audio-engine.test.ts`).
 */
export class AudioEngine {
  private context: AudioContext | undefined;
  private created = false;
  private readonly buses = new Map<AudioBus, GainNode>();
  /** A second gain after each bus that `duck` lowers, so ducking never fights the player's sliders. */
  private readonly ducks = new Map<AudioBus, GainNode>();
  private duckedUntil = 0;
  private readonly matches = new Map<string, number>();
  private levels: Record<AudioBus, number> = { music: 1, effects: 1, voices: 1 };
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loading = new Map<string, Promise<AudioBuffer | undefined>>();
  private impulse: AudioBuffer | undefined;

  constructor(private readonly create: () => AudioContext | undefined = defaultContext, private readonly load: (url: string) => Promise<ArrayBuffer | undefined> = defaultLoad) {}

  /** The shared context, made on first use. Browsers keep it suspended until the page is touched; the first gesture resumes it. */
  private get audio() {
    if (this.created) return this.context;
    this.created = true;
    const context = this.context = this.create();
    if (!context) return undefined;
    // Everything meets in a fast limiter before the speakers, so a volley of blows that sums past full scale is held, not clipped.
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -2; limiter.knee.value = 0; limiter.ratio.value = 20; limiter.attack.value = .002; limiter.release.value = .2;
    limiter.connect(context.destination);
    for (const bus of BUSES) {
      const gain = context.createGain(), duck = context.createGain();
      gain.gain.value = this.levels[bus]; gain.connect(duck); duck.connect(limiter);
      this.buses.set(bus, gain); this.ducks.set(bus, duck);
    }
    const unlock = () => { if (context.state === 'suspended') void context.resume().catch(() => {}); };
    if (typeof addEventListener === 'function') { addEventListener('pointerdown', unlock); addEventListener('keydown', unlock); }
    return context;
  }

  /** The context when it can make sound now. */
  private get live() { const context = this.audio; return context?.state === 'running' ? context : undefined; }

  /** Sets each bus's final 0..1 level; playing sounds follow within a few milliseconds. */
  setLevels(levels: Record<AudioBus, number>) {
    this.levels = { ...levels };
    const context = this.context;
    if (context) for (const [bus, gain] of this.buses) gain.gain.setTargetAtTime(levels[bus], context.currentTime, .015);
  }

  /**
   * Lowers buses by the given dB for `seconds`, then brings them back; a duck asked while one holds extends it. Attack is quick so a
   * line is clear from its first syllable, release slow so the battle swells back rather than jumping.
   */
  duck(seconds: number, depth: Partial<Record<AudioBus, number>>) {
    const context = this.live;
    if (!context || seconds <= 0) return;
    const now = context.currentTime;
    this.duckedUntil = Math.max(this.duckedUntil, now + seconds);
    for (const [bus, db] of Object.entries(depth) as [AudioBus, number][]) {
      const gain = this.ducks.get(bus)?.gain;
      if (!gain) continue;
      gain.cancelScheduledValues(now); gain.setValueAtTime(gain.value, now);
      gain.setTargetAtTime(10 ** (db / 20), now, .03); gain.setTargetAtTime(1, this.duckedUntil, .25);
    }
  }

  /** The gain that brings a decoded recording to the matched loudness; 1 until it is decoded. */
  match(url: string) { return this.matches.get(url) ?? 1; }

  /** Downloads and decodes sounds once; later calls for the same file share the first. Resolves whether each one is ready. */
  async preload(urls: string[]) { await Promise.all(urls.map(url => this.decode(url))); }

  /** The decoded sound, fetching it first if needed; nothing when audio, the format or the download is unavailable (sound stays optional). */
  decode(url: string): Promise<AudioBuffer | undefined> {
    const ready = this.buffers.get(url);
    if (ready) return Promise.resolve(ready);
    let pending = this.loading.get(url);
    if (!pending) {
      const context = this.audio;
      pending = !context ? Promise.resolve(undefined) : this.load(url)
        .then(data => data && context.decodeAudioData(data))
        .then(buffer => { if (buffer) { this.buffers.set(url, buffer); this.matches.set(url, matchGain(loudness(buffer))); } return buffer; }, () => undefined)
        .finally(() => this.loading.delete(url));
      this.loading.set(url, pending);
    }
    return pending;
  }

  /** Seconds a decoded sound lasts at normal speed; 0 when it isn't decoded. */
  duration(url: string) { return this.buffers.get(url)?.duration ?? 0; }

  /** Plays a decoded sound once. Returns a stop function, or nothing when it is not decoded or the page has not been touched yet. */
  play(url: string, { bus = 'effects', volume = 1, rate = 1, pan = 0, reverb = false, space, fade, matched = false }: PlayOptions = {}): StopSound | undefined {
    const buffer = this.buffers.get(url), context = this.live;
    if (!buffer || !context) return undefined;
    if (matched) volume *= this.match(url);
    const source = context.createBufferSource(), gain = context.createGain();
    source.buffer = buffer; source.playbackRate.value = rate; gain.gain.value = volume;
    const start = Math.min(buffer.duration, fade?.start ?? 0), played = Math.min(buffer.duration - start, fade?.length ?? Infinity);
    if (fade) {
      const now = context.currentTime, length = played / rate, up = Math.min(fade.in, length), down = Math.min(fade.out, length);
      gain.gain.setValueAtTime(up > 0 ? 0 : volume, now);
      if (up > 0) gain.gain.linearRampToValueAtTime(volume, now + up);
      gain.gain.setValueAtTime(volume, now + Math.max(up, length - down));
      gain.gain.linearRampToValueAtTime(0, now + length);
    }
    const output = this.buses.get(bus)!, nodes: AudioNode[] = [source, gain];
    source.connect(gain);
    // What the listener hears of the dry sound, placed across the screen: rooms are fed from here so their tail comes from the same side.
    let placed: AudioNode = gain;
    if (pan) { const panner = context.createStereoPanner(); panner.pan.value = Math.max(-1, Math.min(1, pan)); gain.connect(panner); panner.connect(output); nodes.push(panner); placed = panner; }
    else gain.connect(output);
    if (reverb) {
      // The dry sound is joined by a wet copy through a decaying-noise hall, which rings on after the cue ends.
      const hall = context.createConvolver(), wet = context.createGain();
      hall.buffer = this.hall(context); wet.gain.value = 1.4;
      gain.connect(hall); hall.connect(wet); wet.connect(output);
      nodes.push(hall, wet);
    }
    if (space) {
      // Reflections arrive after a short pre-delay, through a natural room whose tail loses its highs as it dies away.
      const delay = context.createDelay(1), room = context.createConvolver(), dark = context.createBiquadFilter(), wet = context.createGain();
      delay.delayTime.value = space.predelay; room.buffer = this.room(context, space.seconds);
      dark.type = 'lowpass'; dark.frequency.value = space.tone; wet.gain.value = space.reverb;
      placed.connect(delay); delay.connect(room); room.connect(dark); dark.connect(wet); wet.connect(output);
      nodes.push(delay, room, dark, wet);
    }
    // The room keeps ringing after the line ends; its nodes go once the tail has died away.
    source.onended = () => { const release = () => { for (const node of nodes) node.disconnect(); }; if (space) setTimeout(release, (space.predelay + space.seconds) * 1000); else release(); };
    source.start(0, start, played);
    let stopped = false;
    return (fadeOut = 0) => {
      if (stopped) return;
      stopped = true;
      const now = context.currentTime;
      if (fadeOut > 0) { gain.gain.cancelScheduledValues(now); gain.gain.setValueAtTime(gain.gain.value, now); gain.gain.linearRampToValueAtTime(0, now + fadeOut); }
      source.stop(now + fadeOut);
    };
  }

  /** A scheduled, stoppable source for sounds that follow a gameplay timeline (channel sounds); `level` scales every volume it is given. */
  controlled(url: string, options: ControlledOptions, bus: AudioBus = 'effects', level = 1) {
    const buffer = this.buffers.get(url), context = this.live;
    if (!buffer || !context || options.seconds <= 0) return undefined;
    const source = context.createBufferSource(), gain = context.createGain(), pan = context.createStereoPanner();
    source.buffer = buffer; source.loop = options.loop; gain.gain.value = options.volume * level; pan.pan.value = options.pan;
    source.connect(gain); gain.connect(pan); pan.connect(this.buses.get(bus)!);
    const at = context.currentTime + options.delay;
    source.start(at, options.loop ? options.offset % buffer.duration : options.offset);
    source.stop(at + options.seconds);
    source.onended = () => { source.disconnect(); gain.disconnect(); pan.disconnect(); };
    let stopped = false;
    return {
      stop: () => { if (!stopped) { stopped = true; source.stop(); } },
      mix: (volume: number, position: number) => { gain.gain.value = volume * level; pan.pan.value = position; },
    };
  }

  /**
   * Loops a decoded sound with no seam: the buffer source wraps sample-exactly from its last frame to its first, which a media
   * element's `loop` does not. `trim` leaves seconds off the head and tail of the loop, for a recording that fades at its ends. It
   * starts silent and follows `level`, at the matched loudness when `matched`. Nothing when it is not decoded or the page is still silent.
   */
  loop(url: string, bus: AudioBus = 'effects', trim = { head: 0, tail: 0 }, matched = false): LoopHandle | undefined {
    const buffer = this.buffers.get(url), context = this.live;
    if (!buffer || !context) return undefined;
    const match = matched ? this.match(url) : 1;
    const source = context.createBufferSource(), gain = context.createGain();
    source.buffer = buffer; source.loop = true; gain.gain.value = 0;
    if (trim.head || trim.tail) { source.loopStart = trim.head; source.loopEnd = buffer.duration - trim.tail; }
    source.connect(gain); gain.connect(this.buses.get(bus)!);
    source.onended = () => { source.disconnect(); gain.disconnect(); };
    source.start(0, trim.head);
    let stopped = false;
    return {
      level: volume => gain.gain.setTargetAtTime(volume * match, context.currentTime, .05),
      stop: () => { if (!stopped) { stopped = true; source.stop(); } },
    };
  }

  /** Streams a long track through `bus`. The element is the transport (play, pause, seek, `loop`, `ended`); `level` is its volume. */
  stream(url: string, bus: AudioBus = 'music'): StreamHandle {
    const element = new Audio(); element.preload = 'none'; element.src = url;
    const context = this.audio;
    if (!context) return { element, level: volume => { element.volume = Math.max(0, Math.min(1, volume)); } };
    const gain = context.createGain(); gain.gain.value = 0;
    context.createMediaElementSource(element).connect(gain); gain.connect(this.buses.get(bus)!);
    return { element, level: volume => { gain.gain.value = volume; } };
  }

  /** The context and a bus input for a sound that builds its own treatment (the spell voice), when it can make sound now. */
  graph(bus: AudioBus): { context: AudioContext; output: AudioNode } | undefined {
    const context = this.live;
    return context && { context, output: this.buses.get(bus)! };
  }

  /** A decoded sound, if it is ready. */
  buffer(url: string) { return this.buffers.get(url); }

  private readonly rooms = new Map<number, AudioBuffer>();
  /** A natural room: early reflections a little apart in each ear, then a smooth exponential tail, decorrelated between the channels. */
  private room(context: AudioContext, seconds: number) {
    const cached = this.rooms.get(seconds);
    if (cached) return cached;
    const length = Math.floor(context.sampleRate * seconds), impulse = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-6.9 * i / length) * (i < context.sampleRate * .08 ? .5 + .5 * Math.random() : .7);
      for (const at of channel ? [.011, .019, .031, .047] : [.013, .023, .029, .043]) data[Math.floor(at * context.sampleRate)] += .6 * (1 - at * 8);
    }
    this.rooms.set(seconds, impulse);
    return impulse;
  }

  private hall(context: AudioContext) {
    if (this.impulse) return this.impulse;
    const seconds = 2.2, length = Math.floor(context.sampleRate * seconds), impulse = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
    }
    return this.impulse = impulse;
  }
}
